import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { applyEndpointAgentStatus } from "./lib/live-agent-status";
import { resolveAgentLoginStatus } from "@shared/agent-status-transition";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { FloatingDialpad, DialpadHeaderButton } from "@/components/floating-dialpad";
import { FloatingCallScript } from "@/components/floating-call-script";
import { MonitorCallPanel } from "@/components/monitor-call-panel";
import { DialpadStateProvider } from "@/lib/dialpad-state";
import { FloatingKbSearch, KbSearchHeaderButton } from "@/components/floating-kb-search";
import { KbSearchStateProvider } from "@/lib/kb-search-state";
import { FloatingTeamChat, TeamChatHeaderButton } from "@/components/floating-team-chat";
import { TeamChatStateProvider } from "@/lib/team-chat-state";
import { FloatingGlobalSearch, GlobalSearchHeaderButton } from "@/components/floating-global-search";
import { GlobalSearchStateProvider } from "@/lib/global-search-state";
import { FloatingCopilot, CopilotHeaderButton } from "@/components/floating-copilot";
import { QuickActionsHeaderButton } from "@/components/quick-actions-header-button";
import { CopilotStateProvider } from "@/lib/copilot-state";
import { PostCallDisposition } from "@/components/post-call-disposition";
import { ThemeToggle } from "@/components/theme-toggle";
import { NotificationBell } from "@/components/notification-bell";
import { ProtectedFeatureRoute } from "@/components/protected-feature-route";
import { PlivoProvider, usePlivo } from "@/lib/plivo-context";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { PermissionsProvider } from "@/lib/permissions-context";
import { NavigationGuardProvider, NavigationGuardDialog } from "@/lib/navigation-guard";
import { Loader2, AlertTriangle, LogOut, Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { lazy, Suspense, useEffect, useRef, useState, useMemo } from "react";
import { usePlatformBranding } from "@/hooks/use-platform-branding";
import { useQuery } from "@tanstack/react-query";
import io from "socket.io-client";
import { getConversationsChannel, releaseConversationsChannel, subscribeToReconnect } from "@/lib/pusher-client";
import { getTenantChannel, releaseTenantChannel } from "@/lib/pusher-client";
import { shouldNotifyForConversationMessage, shouldNotifyForConversationUnassignment, shouldPlayConversationMessageSound, shouldRefreshNotificationSummaryForConversationUnassignment, type AgentStatusChangedPayload, type ConversationUnassignedPayload, type ConversationUpdatedPayload, type DashboardStatsChangedPayload, type NewMessagePayload, type QueueNoAgentWaitingPayload, type TicketUpdatedPayload, type NotificationCreatedPayload, type TenantSettingsChangedPayload, type SoundNotificationSettingsChangedPayload } from "@shared/pusher-events";
import { conversationMessageNotificationDestination } from "@shared/conversation-notification-routing";
import { useNotificationSound, unlockAudioContext, playCustomOrBeep, normalizeNotificationVolume } from "@/hooks/use-notification-sound";
import { isConversationActivelyViewed } from "@/lib/active-conversation-view";
import { useBrowserNotifications } from "@/hooks/use-browser-notifications";
import { useInboxToastNotifications } from "@/hooks/use-inbox-toast-notifications";
const NotFound = lazy(() => import("@/pages/not-found"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const CallHistory = lazy(() => import("@/pages/call-history"));
const Contacts = lazy(() => import("@/pages/contacts"));
const ContactDetail = lazy(() => import("@/pages/contact-detail"));
const Queues = lazy(() => import("@/pages/queues"));
const QueueDetail = lazy(() => import("@/pages/queue-detail"));
const PhoneNumbers = lazy(() => import("@/pages/phone-numbers"));
const PhoneNumberDetail = lazy(() => import("@/pages/phone-number-detail"));
const IvrMenus = lazy(() => import("@/pages/ivr"));
const IvrDetail = lazy(() => import("@/pages/ivr-detail"));
const ContactLists = lazy(() => import("@/pages/contact-lists"));
const BusinessHours = lazy(() => import("@/pages/business-hours"));
const Voicemails = lazy(() => import("@/pages/voicemails"));
const VoicemailStatuses = lazy(() => import("@/pages/voicemail-statuses"));
const PreviewDialer = lazy(() => import("@/pages/preview-dialer"));
const Dispositions = lazy(() => import("@/pages/dispositions"));
const DispositionConversionSettings = lazy(() => import("@/pages/disposition-conversion-settings"));
const Recordings = lazy(() => import("@/pages/recordings"));
const Extensions = lazy(() => import("@/pages/extensions"));
const GeneralSettings = lazy(() => import("@/pages/general-settings"));
const AgentStatuses = lazy(() => import("@/pages/agent-statuses"));
const LoginPage = lazy(() => import("@/pages/login"));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));
const Verify2FAPage = lazy(() => import("@/pages/verify-2fa"));
const Conversations = lazy(() => import("@/pages/conversations"));
const AdminTenants = lazy(() => import("@/pages/admin-tenants"));
const TenantDetail = lazy(() => import("@/pages/tenant-detail"));
const TenantUsers = lazy(() => import("@/pages/tenant-users"));
const Billing = lazy(() => import("@/pages/billing"));
const AgentProfile = lazy(() => import("@/pages/agent-profile"));
const WorkforceManagement = lazy(() => import("@/pages/workforce"));
const DncManagement = lazy(() => import("@/pages/dnc-management"));
const BlockedNumbers = lazy(() => import("@/pages/blocked-numbers"));
const Tickets = lazy(() => import("@/pages/tickets"));
const TicketingSettings = lazy(() => import("@/pages/ticketing-settings"));
const LiveChatSettings = lazy(() => import("@/pages/livechat-settings"));
const WhatsAppSettings = lazy(() => import("@/pages/whatsapp-settings"));
const FacebookSettings = lazy(() => import("@/pages/facebook-settings"));
const InstagramSettings = lazy(() => import("@/pages/instagram-settings"));
const IntegrationSettings = lazy(() => import("@/pages/integration-settings"));
const IntegrationDetail = lazy(() => import("@/pages/integration-detail"));
const TicketHistory = lazy(() => import("@/pages/ticket-history"));
const TicketDashboard = lazy(() => import("@/pages/ticket-dashboard"));
const ConversationHistory = lazy(() => import("@/pages/conversation-history"));
const ConversationDashboard = lazy(() => import("@/pages/conversation-dashboard"));
const CrmPipeline = lazy(() => import("@/pages/crm-pipeline"));
const CrmDealDetail = lazy(() => import("@/pages/crm-deal-detail"));
const CrmSettings = lazy(() => import("@/pages/crm-settings"));
const CrmAnalytics = lazy(() => import("@/pages/crm-analytics"));
const CrmActivities = lazy(() => import("@/pages/crm-activities"));
const CrmActivityTracking = lazy(() => import("@/pages/crm-activity-tracking"));
const Campaigns = lazy(() => import("@/pages/campaigns"));
const CampaignCreate = lazy(() => import("@/pages/campaign-create"));
const CampaignDetail = lazy(() => import("@/pages/campaign-detail"));
const CampaignDashboard = lazy(() => import("@/pages/campaign-dashboard"));
const CampaignWorkflows = lazy(() => import("@/pages/campaign-workflows"));
const CsatSettings = lazy(() => import("@/pages/csat-settings"));
const CsatDashboard = lazy(() => import("@/pages/csat-dashboard"));
const CsatSurveysList = lazy(() => import("@/pages/csat-surveys"));
const CsatSurveyForm = lazy(() => import("@/pages/csat-survey-form"));
const CsatSurveyResults = lazy(() => import("@/pages/csat-survey-results"));
const CustomDashboards = lazy(() => import("@/pages/custom-dashboards"));
const RealtimeDashboard = lazy(() => import("@/pages/realtime-dashboard"));
const WebhookSettings = lazy(() => import("@/pages/webhook-settings"));
const SipForwarding = lazy(() => import("@/pages/sip-forwarding"));
const CustomCallerIdsSettings = lazy(() => import("@/pages/custom-caller-ids-settings"));
const PreviewDialerCampaigns = lazy(() => import("@/pages/preview-dialer-campaigns"));
const PreviewDialerCampaignCreate = lazy(() => import("@/pages/preview-dialer-campaign-create"));
const PreviewDialerDashboard = lazy(() => import("@/pages/preview-dialer-dashboard"));
const PreviewDialerCampaignDetail = lazy(() => import("@/pages/preview-dialer-campaign-detail"));
const DialerScripts = lazy(() => import("@/pages/dialer-scripts"));
const ScreenPopSettings = lazy(() => import("@/pages/screen-pop-settings"));
const ScreenPopScripts = lazy(() => import("@/pages/screen-pop-scripts"));
const ScreenPopExternalLinks = lazy(() => import("@/pages/screen-pop-external-links"));
const SmsCampaignsPage = lazy(() => import("@/pages/sms-campaigns"));
const SmsCampaignForm = lazy(() => import("@/pages/sms-campaign-form"));
const EmailCampaignsPage = lazy(() => import("@/pages/email-campaigns"));
const EmailSenderSignaturesPage = lazy(() => import("@/pages/email-sender-signatures"));
const EmailTemplatesPage = lazy(() => import("@/pages/email-templates"));
const EmailTemplateEditorPage = lazy(() => import("@/pages/email-template-editor"));
const EmailCampaignFormPage = lazy(() => import("@/pages/email-campaign-form"));
const CannedResponses = lazy(() => import("@/pages/canned-responses"));
const CustomerSupport = lazy(() => import("@/pages/customer-support"));
const LoadTesting = lazy(() => import("@/pages/load-testing"));
const PlatformSettings = lazy(() => import("@/pages/platform-settings"));
const AdminWhiteLabel = lazy(() => import("@/pages/admin-white-label"));
const KnowledgeBase = lazy(() => import("@/pages/knowledge-base"));
const CrmAutomations = lazy(() => import("@/pages/crm-automations"));
const CrmAutomationBuilder = lazy(() => import("@/pages/crm-automation-builder"));
const CrmAutomationLogs = lazy(() => import("@/pages/crm-automation-logs"));
const Reports = lazy(() => import("@/pages/reports"));
const KbManager = lazy(() => import("@/pages/kb-manager"));
const KbSearch = lazy(() => import("@/pages/kb-search"));
const AgentOverview = lazy(() => import("@/pages/agent-overview"));
const AgentStatusHistory = lazy(() => import("@/pages/agent-status-history"));
const NotificationSettings = lazy(() => import("@/pages/notification-settings"));
const TeamChat = lazy(() => import("@/pages/team-chat"));
const ApiDocsPage = lazy(() => import("@/pages/api-docs"));
const ApiKeysPage = lazy(() => import("@/pages/api-keys"));
const AiBotSettings = lazy(() => import("@/pages/ai-bot-settings"));
const AuditLog = lazy(() => import("@/pages/audit-log"));
const AiBotActions = lazy(() => import("@/pages/ai-bot-actions"));
const VoiceAgents = lazy(() => import("@/pages/voice-agents"));
const VoiceAgentCreatePage = lazy(() => import("@/pages/voice-agent-create"));
const VoiceAgentEditPage = lazy(() => import("@/pages/voice-agent-edit"));
const VoiceFlowsPage = lazy(() => import("@/pages/voice-flows"));
const VoiceAiAnalyticsPage = lazy(() => import("@/pages/voice-ai-analytics"));
const PermissionsPage = lazy(() => import("@/pages/permissions"));
const AiAnalysisDashboard = lazy(() => import("@/pages/ai-analysis-dashboard"));
const AiAnalysisCalls = lazy(() => import("@/pages/ai-analysis-calls"));
const AiAnalysisLeaderboard = lazy(() => import("@/pages/ai-analysis-leaderboard"));
const AiAnalysisTeam = lazy(() => import("@/pages/ai-analysis-team"));
const AiAnalysisTrends = lazy(() => import("@/pages/ai-analysis-trends"));
const AiAnalysisOneOnOne = lazy(() => import("@/pages/ai-analysis-one-on-one"));
const AiAnalysisSettings = lazy(() => import("@/pages/ai-analysis-settings"));
import { SupportChatWidget } from "@/components/support-chat-widget";
import { CampaignCallIndicator } from "@/components/campaign-indicator";
import { useActivityNotifications } from "@/hooks/use-activity-notifications";
import { useLocation } from "wouter";

const roleLabel: Record<string, string> = {
  super_admin: "Super Admin",
  tenant_admin: "Admin",
  supervisor: "Supervisor",
  agent: "Agent",
};

function UserHeaderInfo() {
  const { user, logout, isImpersonating } = useAuth();
  const { disconnect } = usePlivo();
  return (
    <div className="flex items-center gap-2">
      <div className="text-right">
        <p className="text-xs font-medium leading-none" data-testid="text-user-display-name">{user?.displayName}</p>
        <p className="text-[10px] text-muted-foreground">
          {isImpersonating ? "Viewing as Tenant Admin" : (roleLabel[user?.role || ""] || user?.role)}
        </p>
      </div>
      <Button size="icon" variant="ghost" onClick={() => { disconnect(); logout(); }} data-testid="button-logout">
        <LogOut className="w-4 h-4" />
      </Button>
    </div>
  );
}


function Router() {
  const { user, isImpersonating } = useAuth();
  useActivityNotifications(!!user);
  const { data: voiceAiStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/voice-ai/enabled"],
    enabled: !!user && (user.role === "tenant_admin" || user.role === "super_admin"),
  });
  const voiceAiRouteEnabled = voiceAiStatus?.enabled === true;

  return (
    <Suspense fallback={(
      <div className="flex min-h-[240px] items-center justify-center" data-testid="route-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )}>
      <Switch>
      <Route path="/" component={Dashboard} />
      <Route path="/conversations" component={Conversations} />
      <Route path="/conversations/active" component={Conversations} />
      <Route path="/conversations/inbox" component={ConversationHistory} />
      <Route path="/conversations/history" component={ConversationHistory} />
      <Route path="/conversation-dashboard" component={ConversationDashboard} />
      <Route path="/conversation-history" component={ConversationHistory} />
      <Route path="/calls" component={CallHistory} />
      <Route path="/contacts" component={Contacts} />
      <Route path="/contacts/:id" component={ContactDetail} />
      <Route path="/contact-lists" component={ContactLists} />
      <Route path="/dnc" component={DncManagement} />
      <Route path="/blocked-numbers" component={BlockedNumbers} />
      <Route path="/queues" component={Queues} />
      <Route path="/queues/:id" component={QueueDetail} />
      <Route path="/phone-numbers" component={PhoneNumbers} />
      <Route path="/phone-numbers/:id" component={PhoneNumberDetail} />
      <Route path="/ivr" component={IvrMenus} />
      <Route path="/ivr/:id" component={IvrDetail} />
      <Route path="/business-hours" component={BusinessHours} />
      <Route path="/voicemails" component={Voicemails} />
      <Route path="/voicemail-statuses" component={VoicemailStatuses} />
      <Route path="/reports" component={Reports} />
      <Route path="/kb-manager">
        {() => (
          <ProtectedFeatureRoute feature="knowledgeBaseManager">
            <KbManager />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/kb-search">
        {() => (
          <ProtectedFeatureRoute feature="knowledgeBaseSearch">
            <KbSearch />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/dispositions" component={Dispositions} />
      <Route path="/disposition-conversion-settings" component={DispositionConversionSettings} />
      <Route path="/recordings" component={Recordings} />
      <Route path="/extensions" component={Extensions} />
      <Route path="/general-settings" component={GeneralSettings} />
      <Route path="/agent-statuses" component={AgentStatuses} />
      <Route path="/users" component={TenantUsers} />
      <Route path="/users/:userId/overview" component={AgentOverview} />
      <Route path="/users/:userId/status-history" component={AgentStatusHistory} />
      <Route path="/billing" component={Billing} />
      <Route path="/agent-profile" component={AgentProfile} />
      <Route path="/workforce" component={WorkforceManagement} />
      <Route path="/tickets" component={Tickets} />
      <Route path="/ticket-dashboard" component={TicketDashboard} />
      <Route path="/ticket-history" component={TicketHistory} />
      <Route path="/ticketing-settings" component={TicketingSettings} />
      <Route path="/livechat-settings" component={LiveChatSettings} />
      <Route path="/whatsapp-settings">
        {() => (
          <ProtectedFeatureRoute feature="whatsapp">
            <WhatsAppSettings />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/facebook-settings" component={FacebookSettings} />
      <Route path="/instagram-settings" component={InstagramSettings} />
      <Route path="/crm-pipeline" component={CrmPipeline} />
      <Route path="/crm-deals/:id" component={CrmDealDetail} />
      <Route path="/crm-settings" component={CrmSettings} />
      <Route path="/crm-analytics/:pipelineId" component={CrmAnalytics} />
      <Route path="/crm-activities" component={CrmActivities} />
      <Route path="/crm-activity-tracking" component={CrmActivityTracking} />
      <Route path="/crm-automations" component={CrmAutomations} />
      <Route path="/crm-automations/:id/logs" component={CrmAutomationLogs} />
      <Route path="/crm-automations/:id" component={CrmAutomationBuilder} />
      <Route path="/campaign-dashboard" component={CampaignDashboard} />
      <Route path="/custom-dashboards" component={CustomDashboards} />
      <Route path="/custom-dashboards/:id" component={CustomDashboards} />
      <Route path="/realtime-dashboard" component={RealtimeDashboard} />
      <Route path="/campaigns" component={Campaigns} />
      <Route path="/campaigns/new" component={CampaignCreate} />
      <Route path="/campaigns/:id/edit" component={CampaignCreate} />
      <Route path="/campaigns/:id" component={CampaignDetail} />
      <Route path="/campaign-workflows" component={CampaignWorkflows} />
      <Route path="/csat-settings" component={CsatSettings} />
      <Route path="/csat-dashboard" component={CsatDashboard} />
      <Route path="/csat-surveys" component={CsatSurveysList} />
      <Route path="/csat-surveys/new" component={CsatSurveyForm} />
      <Route path="/csat-surveys/:id/edit" component={CsatSurveyForm} />
      <Route path="/csat-survey-results" component={CsatSurveyResults} />
      <Route path="/preview-dialer" component={PreviewDialer} />
      <Route path="/preview-dialer-dashboard" component={PreviewDialerDashboard} />
      <Route path="/preview-dialer-campaigns" component={PreviewDialerCampaigns} />
      <Route path="/preview-dialer-campaigns/create" component={PreviewDialerCampaignCreate} />
      <Route path="/preview-dialer-campaigns/:id" component={PreviewDialerCampaignDetail} />
      <Route path="/preview-dialer-campaigns/:id/edit" component={PreviewDialerCampaignCreate} />
      <Route path="/dialer-scripts" component={DialerScripts} />
      <Route path="/screen-pop-settings" component={ScreenPopSettings} />
      <Route path="/screen-pop-scripts" component={ScreenPopScripts} />
      <Route path="/screen-pop-external-links" component={ScreenPopExternalLinks} />
      <Route path="/webhook-settings" component={WebhookSettings} />
      <Route path="/integration-settings/:providerKey" component={IntegrationDetail} />
      <Route path="/integration-settings" component={IntegrationSettings} />
      <Route path="/sip-forwarding" component={SipForwarding} />
      <Route path="/custom-caller-ids" component={CustomCallerIdsSettings} />
      <Route path="/notification-settings">
        {() => (
          <ProtectedFeatureRoute feature="notifications">
            <NotificationSettings />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/api-keys" component={ApiKeysPage} />
      <Route path="/api-docs" component={ApiDocsPage} />
      <Route path="/audit-log" component={AuditLog} />
      <Route path="/ai-bot" component={AiBotSettings} />
      <Route path="/ai-bot/actions" component={AiBotActions} />
      {(user?.role === "tenant_admin" || user?.role === "super_admin") && voiceAiRouteEnabled && (
        <>
          <Route path="/voice-agents" component={VoiceAgents} />
          <Route path="/voice-agents/new" component={VoiceAgentCreatePage} />
          <Route path="/voice-agents/:id/edit" component={VoiceAgentEditPage} />
        </>
      )}
      {(user?.role === "tenant_admin" || user?.role === "super_admin") && voiceAiRouteEnabled && (
        <Route path="/voice-flows" component={VoiceFlowsPage} />
      )}
      {(user?.role === "tenant_admin" || user?.role === "super_admin") && voiceAiRouteEnabled && (
        <Route path="/voice-ai-analytics" component={VoiceAiAnalyticsPage} />
      )}
      {(user?.role === "tenant_admin" || isImpersonating) && (
        <Route path="/permissions" component={PermissionsPage} />
      )}
      <Route path="/ai-analysis" component={AiAnalysisDashboard} />
      <Route path="/ai-analysis/calls" component={AiAnalysisCalls} />
      <Route path="/ai-analysis/leaderboard" component={AiAnalysisLeaderboard} />
      <Route path="/ai-analysis/team" component={AiAnalysisTeam} />
      <Route path="/ai-analysis/trends" component={AiAnalysisTrends} />
      <Route path="/ai-analysis/one-on-one" component={AiAnalysisOneOnOne} />
      <Route path="/ai-analysis/settings" component={AiAnalysisSettings} />
      <Route path="/team-chat" component={TeamChat} />
      <Route path="/load-testing">
        {() => (
          user?.role === "super_admin" && !isImpersonating
            ? <LoadTesting />
            : (
              <div className="flex min-h-[400px] flex-col items-center justify-center p-6 text-center text-muted-foreground">
                <AlertTriangle className="mb-4 h-12 w-12 opacity-30" />
                <h1 className="mb-1 text-lg font-semibold text-foreground">Access denied</h1>
                <p className="text-sm" data-testid="load-testing-access-denied">
                  You do not have permission to open this page.
                </p>
              </div>
            )
        )}
      </Route>
      <Route path="/sms-campaigns">
        {() => (
          <ProtectedFeatureRoute feature="smsCampaigns">
            <SmsCampaignsPage />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/sms-campaigns/new">
        {() => (
          <ProtectedFeatureRoute feature="smsCampaigns">
            <SmsCampaignForm />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/sms-campaigns/:id/edit">
        {(params) => (
          <ProtectedFeatureRoute feature="smsCampaigns">
            <SmsCampaignForm params={params} />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/email-campaigns">
        {() => (
          <ProtectedFeatureRoute feature="emailCampaigns">
            <EmailCampaignsPage />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/email-campaigns/sender-signatures">
        {() => (
          <ProtectedFeatureRoute feature="emailCampaigns">
            <EmailSenderSignaturesPage />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/email-campaigns/templates">
        {() => (
          <ProtectedFeatureRoute feature="emailCampaigns">
            <EmailTemplatesPage />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/email-campaigns/templates/new">
        {() => (
          <ProtectedFeatureRoute feature="emailCampaigns">
            <EmailTemplateEditorPage />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/email-campaigns/templates/:id/edit">
        {() => (
          <ProtectedFeatureRoute feature="emailCampaigns">
            <EmailTemplateEditorPage />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/email-campaigns/new">
        {() => (
          <ProtectedFeatureRoute feature="emailCampaigns">
            <EmailCampaignFormPage />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/email-campaigns/:id/edit">
        {(params) => (
          <ProtectedFeatureRoute feature="emailCampaigns">
            <EmailCampaignFormPage params={params} />
          </ProtectedFeatureRoute>
        )}
      </Route>
      <Route path="/sms-templates">{() => <CannedResponses channel="sms" />}</Route>
      <Route path="/ticket-templates">{() => <CannedResponses channel="ticket" />}</Route>
      {user?.role === "super_admin" && (
        <>
          <Route path="/admin/tenants" component={AdminTenants} />
          <Route path="/admin/tenants/:id" component={TenantDetail} />
          <Route path="/admin/settings" component={PlatformSettings} />
          <Route path="/admin/white-label" component={AdminWhiteLabel} />
          <Route path="/customer-support" component={CustomerSupport} />
        </>
      )}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function ImpersonationBanner() {
  const { isImpersonating, impersonatingTenantName, exitImpersonation } = useAuth();
  const [, navigate] = useLocation();

  if (!isImpersonating) return null;

  const handleExit = async () => {
    await exitImpersonation();
    navigate("/admin/tenants");
  };

  return (
    <div
      className="bg-amber-600 text-white px-4 py-2 flex items-center justify-between gap-4 sticky top-0 z-[100]"
      data-testid="banner-impersonation"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="text-sm font-medium">
          Viewing as Tenant Admin: {impersonatingTenantName}
        </span>
        <span className="text-sm opacity-80 hidden sm:inline">
          — You are currently impersonating a tenant account
        </span>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExit}
        className="border-white/50 text-white bg-transparent shrink-0"
        data-testid="button-exit-impersonation"
      >
        <LogOut className="w-3.5 h-3.5 mr-1.5" /> Exit Impersonation
      </Button>
    </div>
  );
}

function BrowserNotificationBanner() {
  const { permission, showPrompt, requestPermission, dismissPrompt } = useBrowserNotifications();
  if (!showPrompt) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-950/30 border-b text-sm shrink-0">
      <Bell className="w-4 h-4 text-blue-500 shrink-0" />
      <span className="flex-1 text-blue-700 dark:text-blue-300">
        Enable browser notifications to get alerts for new messages and chats — even when this tab is in the background.
      </span>
      <Button size="sm" variant="outline" className="shrink-0" onClick={requestPermission} data-testid="button-enable-browser-notifications">
        Enable
      </Button>
      <Button size="sm" variant="ghost" className="shrink-0" onClick={dismissPrompt} data-testid="button-dismiss-notification-prompt">
        Dismiss
      </Button>
    </div>
  );
}

const CHANNEL_LABELS: Record<string, string> = {
  sms: "SMS",
  whatsapp: "WhatsApp",
  livechat: "Live Chat",
  facebook: "Facebook",
  instagram: "Instagram",
  email: "Email",
};

const INBOX_CHANNEL_FILTERS = new Set(["sms", "livechat", "whatsapp", "facebook", "instagram"]);

function inboxNotificationPath(filter: "unread" | "unassigned", channel?: string | null): string {
  const params = new URLSearchParams({ filter });
  const channelKey = String(channel || "").toLowerCase();
  if (INBOX_CHANNEL_FILTERS.has(channelKey)) {
    params.set("channel", channelKey);
  }
  return `/conversations/inbox?${params.toString()}`;
}

function messageNotificationPath(message: Pick<NewMessagePayload, "conversationId" | "channel" | "assigneeUserId">, currentUserId: string | null | undefined): string {
  if (conversationMessageNotificationDestination(message, currentUserId) === "assigned") {
    const params = new URLSearchParams({ tab: "assigned" });
    if (message.conversationId) params.set("selected", message.conversationId);
    return `/conversations/active?${params.toString()}`;
  }

  return inboxNotificationPath("unread", message.channel);
}

function formatUnassignedConversationContext(event: ConversationUnassignedPayload): string {
  const channelKey = String(event.channel || "").toLowerCase();
  const channelLabel = CHANNEL_LABELS[channelKey] || String(event.channel || "Conversation").toUpperCase();
  const isLivechat = channelKey === "livechat";
  const contact = isLivechat
    ? (event.contactName || event.sourceAddress || "Visitor")
    : (event.sourceAddress
      ? (event.contactName ? `${event.sourceAddress} (${event.contactName})` : event.sourceAddress)
      : (event.contactName || "Unknown contact"));
  const inboxAddress = isLivechat ? (event.widgetName || null) : event.inboxAddress;
  const inbox = inboxAddress ? `Inbox: ${inboxAddress}` : null;
  return [`${channelLabel}: ${contact}`, inbox].filter(Boolean).join("\n");
}

function claimPusherEvent(seenEventIds: Set<string>, eventId: string | null | undefined): boolean {
  if (!eventId) return true;
  if (seenEventIds.has(eventId)) return false;
  seenEventIds.add(eventId);
  // Pusher events are not replayed after reconnect. This bounded guard only
  // protects against duplicate delivery while the tab remains open.
  if (seenEventIds.size > 100) seenEventIds.clear();
  return true;
}

function BrowserNotificationMonitor() {
  const { user } = useAuth();
  const { notify } = useBrowserNotifications();
  const handledUnassignmentIdsRef = useRef<Set<string>>(new Set());
  const activeTenantId = user?.impersonating?.tenantId || user?.tenantId;
  const { data: voiceAiStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/voice-ai/enabled"],
    enabled: !!activeTenantId,
  });
  const canReceiveVoiceAiAlerts = voiceAiStatus?.enabled === true;
  // Use a ref so the socket effect never needs to re-run just because notify's
  // identity changed (which happens when `enabled` toggles). Without this the
  // socket tears down and reconnects every time the user clicks the Enable
  // button, creating a window where notifications can be missed.
  const notifyRef = useRef(notify);
  useEffect(() => { notifyRef.current = notify; }, [notify]);

  useEffect(() => {
    // Super-admins without active impersonation have their platform-tenant tenantId,
    // not a client tenant — skip subscription to avoid spurious failed Pusher auth.
    if (user?.role === "super_admin" && !user?.impersonating) return;
    const tenantId = user?.impersonating?.tenantId || user?.tenantId;
    if (!tenantId) return;

    const pusherChannel = getConversationsChannel(tenantId);
    const operationsChannel = getTenantChannel(tenantId);
    const handleBnNewMsg = (msg: any) => {
      const currentUserId = user?.id;
      const isViewingConversation = Boolean(
        currentUserId
        && msg?.conversationId
        && isConversationActivelyViewed(currentUserId, msg.conversationId),
      );
      if (!shouldNotifyForConversationMessage(msg, currentUserId, isViewingConversation)) return;
      const channelKey = String(msg?.channel || "").toLowerCase();
      const isLivechat = channelKey === "livechat";
      const channelLabel = CHANNEL_LABELS[channelKey] || String(msg?.channel || "Message").toUpperCase();
      const destination = conversationMessageNotificationDestination(msg, currentUserId);
      const fromDisplay = isLivechat
        ? (msg?.contactName || msg?.fromAddress || "Visitor")
        : (msg?.fromAddress
          ? (msg?.contactName ? `${msg.fromAddress} (${msg.contactName})` : msg.fromAddress)
          : null);
      const toDisplay = isLivechat
        ? (msg?.widgetName || null)
        : (msg?.toAddress
          ? (msg?.toNumberFriendlyName ? `${msg.toAddress} (${msg.toNumberFriendlyName})` : msg.toAddress)
          : null);
      const from = fromDisplay ? `From: ${fromDisplay}` : null;
      const to = toDisplay ? `To: ${toDisplay}` : null;
      const text = String(msg?.body || msg?.content || msg?.text || "").trim();
      const bodyLines = [from, to, text ? `Message: ${text.slice(0, destination === "assigned" ? 50 : 80)}` : null].filter(Boolean).join("\n");
      notifyRef.current(destination === "assigned" ? `New assigned ${channelLabel} message` : `Incoming ${channelLabel} Message`, {
        body: bodyLines || "You have a new inbound message.",
        tag: `msg-${msg?.conversationId || msg?.id}`,
      });
    };
    const handleBnConversationUnassigned = (event: ConversationUnassignedPayload) => {
      if (!shouldNotifyForConversationUnassignment(event)) return;
      if (!claimPusherEvent(handledUnassignmentIdsRef.current, event.eventId)) return;
      notifyRef.current("Conversation unassigned", {
        body: formatUnassignedConversationContext(event) || "A conversation was returned to the Inbox.",
        tag: `conversation-unassigned-${event.conversationId}`,
      });
    };
    const handleBnNewVoicemail = (data: any) => {
      const from = data?.callerNumber ? `From: ${data.callerNumber}` : null;
      const to = data?.toNumber ? `To: ${data.toNumber}` : null;
      const queue = data?.queueName ? `Queue: ${data.queueName}` : null;
      const bodyLines = [from, to, queue].filter(Boolean).join("\n");
      notifyRef.current("New Voicemail", {
        body: bodyLines || "A new voicemail was left.",
        tag: `voicemail-${data?.callLogId}`,
      });
    };
    const handleBnNewTicket = (data: any) => {
      const from = data?.fromEmail ? `From: ${data.fromEmail}` : null;
      const to = data?.toEmail ? `To: ${data.toEmail}` : null;
      const subject = data?.subject ? `Subject: ${String(data.subject).slice(0, 80)}` : null;
      const bodyLines = [from, to, subject].filter(Boolean).join("\n");
      notifyRef.current("New Email Ticket", {
        body: bodyLines || "A new email ticket arrived.",
        tag: `ticket-${data?.ticketId}`,
      });
    };
    const handleBnNotification = (data: any) => {
      if (data?.title) {
        notifyRef.current(data.title, { body: data.message || data.body || undefined, tag: `notif-${data?.type}` });
      }
    };
    if (pusherChannel) pusherChannel.bind("new-message", handleBnNewMsg);
    if (pusherChannel) pusherChannel.bind("conversation-unassigned", handleBnConversationUnassigned);
    if (operationsChannel) {
      operationsChannel.bind("new-voicemail", handleBnNewVoicemail);
      operationsChannel.bind("new-ticket", handleBnNewTicket);
      operationsChannel.bind("notification", handleBnNotification);
    }

    // Socket.IO remains only for Voice AI escalation notifications. Do not open
    // a Socket.IO connection when the active tenant does not have Voice AI.
    const socket = canReceiveVoiceAiAlerts
      ? io({ path: "/socket.io", transports: ["websocket"] })
      : null;

    if (socket) {
      socket.on("voice-ai-escalation", (data: any) => {
        const body = data?.summary
          ? `Caller ${data.callerNumber}: ${data.summary}`
          : `Caller ${data?.callerNumber} requested a human agent.`;
        notifyRef.current("Voice AI — Escalation", { body, tag: `escalation-${data?.callUuid}` });
      });

      socket.on("voice-ai-escalation-update", (data: any) => {
        if (data?.summary) {
          notifyRef.current("Voice AI — Escalation Update", {
            body: `Summary updated: ${data.summary}`,
            tag: `escalation-${data?.callUuid}`,
          });
        }
      });
    }

    const unsubReconnect = subscribeToReconnect(() => {
      queryClient.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/conversations") });
    });

    return () => {
      if (pusherChannel) pusherChannel.unbind("new-message", handleBnNewMsg);
      if (pusherChannel) pusherChannel.unbind("conversation-unassigned", handleBnConversationUnassigned);
      if (operationsChannel) {
        operationsChannel.unbind("new-voicemail", handleBnNewVoicemail);
        operationsChannel.unbind("new-ticket", handleBnNewTicket);
        operationsChannel.unbind("notification", handleBnNotification);
      }
      releaseConversationsChannel(tenantId);
      releaseTenantChannel(tenantId);
      socket?.disconnect();
      unsubReconnect();
    };
  }, [user?.tenantId, user?.impersonating?.tenantId, canReceiveVoiceAiAlerts]);

  return null;
}

function GlobalMessageToastMonitor() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [location, navigate] = useLocation();
  const { smsEnabled, vmEnabled } = useInboxToastNotifications();

  const smsEnabledRef = useRef(smsEnabled);
  const vmEnabledRef  = useRef(vmEnabled);
  useEffect(() => { smsEnabledRef.current = smsEnabled; }, [smsEnabled]);
  useEffect(() => { vmEnabledRef.current  = vmEnabled;  }, [vmEnabled]);

  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);
  const handledUnassignmentIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Super-admins without active impersonation have their platform-tenant tenantId,
    // not a client tenant — skip subscription to avoid spurious failed Pusher auth.
    if (user?.role === "super_admin" && !user?.impersonating) return;
    const tenantId = user?.impersonating?.tenantId || user?.tenantId;
    if (!tenantId) return;

    const toastPusherChannel = getConversationsChannel(tenantId);
    const toastOperationsChannel = getTenantChannel(tenantId);
    const handleToastNewMsg = (msg: any) => {
      // While the AI bot is handling this conversation, suppress all notification
      // signals (bell count, toasts, sounds). After handoff handledByBot becomes
      // false and all signals fire normally.
      if (msg?.handledByBot) return;
      if (msg?.direction !== "outbound") {
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      }
      if (!smsEnabledRef.current) return;
      if (msg?.direction === "outbound") return;
      if (locationRef.current.startsWith("/conversations")) return;
      const currentUserId = user?.id;
      const isViewingConversation = Boolean(
        currentUserId
        && msg?.conversationId
        && isConversationActivelyViewed(currentUserId, msg.conversationId),
      );
      if (!shouldNotifyForConversationMessage(msg, currentUserId, isViewingConversation)) return;

      const channelKey = String(msg?.channel || "").toLowerCase();
      const channelLabel = CHANNEL_LABELS[channelKey] || String(msg?.channel || "Message").toUpperCase();
      const destination = conversationMessageNotificationDestination(msg, currentUserId);
      const isLivechat = channelKey === "livechat";
      const contact = msg?.contactName || (isLivechat ? msg?.fromAddress || "Visitor" : msg?.fromAddress) || "Unknown";
      const text = String(msg?.body || msg?.content || msg?.text || "").trim();
      const previewLimit = destination === "assigned" ? 50 : 80;
      const preview = text.length > previewLimit ? text.slice(0, previewLimit) + "…" : text;
      const conversationId = msg?.conversationId;

      toast({
        title: destination === "assigned" ? `New assigned ${channelLabel} message` : `Incoming ${channelLabel} Message`,
        description: [contact, preview].filter(Boolean).join(" — ") || "New inbound message",
        duration: 6000,
        action: conversationId ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigateRef.current(messageNotificationPath(msg, currentUserId))}
            data-testid={`toast-view-msg-${conversationId}`}
          >
            View
          </Button>
        ) : undefined,
      } as any);
    };
    const handleToastConversationUnassigned = (event: ConversationUnassignedPayload) => {
      // Every queue-return event refreshes the bell. AI bot handoffs remain
      // silent for toast/sound/browser surfaces, but must still become visible
      // in the shared Unassigned notification count.
      if (shouldRefreshNotificationSummaryForConversationUnassignment(event)) {
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      }
      if (!shouldNotifyForConversationUnassignment(event)) return;
      if (!claimPusherEvent(handledUnassignmentIdsRef.current, event.eventId)) return;
      if (!smsEnabledRef.current) return;

      const conversationId = event.conversationId;
      const context = formatUnassignedConversationContext(event).replace("\n", " — ");
      toast({
        title: "Conversation unassigned",
        description: context || "A conversation was returned to the Inbox.",
        duration: 6000,
        action: conversationId ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigateRef.current(inboxNotificationPath("unassigned", event.channel))}
            data-testid={`toast-view-unassigned-conversation-${conversationId}`}
          >
            View
          </Button>
        ) : undefined,
      } as any);
    };
    const handleToastConversationUpdated = (event: ConversationUpdatedPayload) => {
      // Assignment changes remove shared-Inbox unread items from every other
      // agent's notification summary. The assigned agent's own unread item is
      // recalculated by the same endpoint from its assigned-conversation set.
      if (!event?.conversationId) return;
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    };
    const handleToastNewVoicemail = (vm: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({
        predicate: (q) => (q.queryKey[0] as string).startsWith("/api/voicemails?"),
      });
      if (!vmEnabledRef.current) return;
      const caller = vm?.fromNumber || vm?.callerName || vm?.callerNumber || "Unknown caller";
      const duration = vm?.duration ? ` (${vm.duration}s)` : "";
      toast({
        title: "New Voicemail",
        description: `${caller}${duration} left a voicemail`,
        duration: 6000,
      });
    };
    const handleToastNewTicket = () => {
      // Refresh bell badge only — no toast for tickets.
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    };
    const handleVoicemailReadStateChanged = (data: {
      id: string;
      vmIsNew?: boolean;
      vmArchived?: boolean;
      vmStatus?: string | null;
      vmNotes?: string | null;
      vmHandledBy?: string | null;
    }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.setQueriesData<{ data: any[]; total: number; page: number; limit: number }>(
        { predicate: (q) => (q.queryKey[0] as string).startsWith("/api/voicemails?") },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((vm: any) => vm.id === data.id
              ? {
                  ...vm,
                  ...(data.vmIsNew !== undefined ? { vmIsNew: data.vmIsNew } : {}),
                  ...(data.vmArchived !== undefined ? { vmArchived: data.vmArchived } : {}),
                  ...(data.vmStatus !== undefined ? { vmStatus: data.vmStatus } : {}),
                  ...(data.vmNotes !== undefined ? { vmNotes: data.vmNotes } : {}),
                  ...(data.vmHandledBy !== undefined ? { vmHandledBy: data.vmHandledBy } : {}),
                }
              : vm),
          };
        }
      );
    };
    const handleVoicemailLifecycleChanged = () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({
        predicate: (q) => (q.queryKey[0] as string).startsWith("/api/voicemails?"),
      });
    };
    const handleVoicemailStatusChanged = () => {
      queryClient.invalidateQueries({ queryKey: ["/api/voicemail-statuses"] });
    };
    if (toastPusherChannel) toastPusherChannel.bind("new-message", handleToastNewMsg);
    if (toastPusherChannel) toastPusherChannel.bind("conversation-unassigned", handleToastConversationUnassigned);
    if (toastPusherChannel) toastPusherChannel.bind("conversation-updated", handleToastConversationUpdated);
    if (toastOperationsChannel) {
      toastOperationsChannel.bind("new-voicemail", handleToastNewVoicemail);
      toastOperationsChannel.bind("new-ticket", handleToastNewTicket);
      toastOperationsChannel.bind("voicemail-read-state-changed", handleVoicemailReadStateChanged);
      toastOperationsChannel.bind("voicemail-lifecycle-changed", handleVoicemailLifecycleChanged);
      toastOperationsChannel.bind("voicemail-status-changed", handleVoicemailStatusChanged);
    }

    const unsubReconnectToast = subscribeToReconnect(() => {
      queryClient.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/conversations") });
    });

    return () => {
      if (toastPusherChannel) toastPusherChannel.unbind("new-message", handleToastNewMsg);
      if (toastPusherChannel) toastPusherChannel.unbind("conversation-unassigned", handleToastConversationUnassigned);
      if (toastPusherChannel) toastPusherChannel.unbind("conversation-updated", handleToastConversationUpdated);
      if (toastOperationsChannel) {
        toastOperationsChannel.unbind("new-voicemail", handleToastNewVoicemail);
        toastOperationsChannel.unbind("new-ticket", handleToastNewTicket);
        toastOperationsChannel.unbind("voicemail-read-state-changed", handleVoicemailReadStateChanged);
        toastOperationsChannel.unbind("voicemail-lifecycle-changed", handleVoicemailLifecycleChanged);
        toastOperationsChannel.unbind("voicemail-status-changed", handleVoicemailStatusChanged);
      }
      releaseConversationsChannel(tenantId);
      releaseTenantChannel(tenantId);
      unsubReconnectToast();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenantId, user?.impersonating?.tenantId]);

  return null;
}

function SipAutoConnect() {
  const { autoConnect, isRegistered } = usePlivo();
  const { user } = useAuth();
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAttemptRef = useRef<number>(0);

  useEffect(() => {
    // Only attempt auto-connect when there is an authenticated user.
    // Guarding here prevents a race where the Plivo SDK fires onLogout
    // during component unmount (or due to a brief network blip right
    // before session teardown), which would trigger a reconnect that
    // sets the agent's status back to "ready" after an explicit logout
    // — causing a spurious system_connection_lost history entry later.
    if (!isRegistered && user) {
      const now = Date.now();
      const timeSinceLast = now - lastAttemptRef.current;
      const delay = lastAttemptRef.current === 0 ? 0 : Math.max(3000 - timeSinceLast, 0);

      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        lastAttemptRef.current = Date.now();
        autoConnect();
        retryTimerRef.current = null;
      }, delay);
    }

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [autoConnect, isRegistered, user]);

  return null;
}

const QUEUE_DEDUP_LS_KEY_APP = "qna_dedup_keys";
const QUEUE_DEDUP_TTL_APP = 10 * 60 * 1000;

function GlobalInboxSoundMonitor() {
  const [location] = useLocation();
  const onConversationsPage = location.startsWith("/conversations");
  const { enabled: soundEnabled } = useNotificationSound("conversations");
  const { user } = useAuth();
  const { toast } = useToast();
  const { isRegistered, connectedEndpointId, callState, autoConnect } = usePlivo();
  const { data: soundSettings } = useQuery<any>({ queryKey: ["/api/notification-settings/sound"] });

  // Refs so the socket effect can always read the latest values without
  // being in its dependency array (which would recreate the socket on every refetch)
  const soundSettingsRef = useRef<any>(null);
  const userRef = useRef<any>(null);
  useEffect(() => { soundSettingsRef.current = soundSettings ?? null; }, [soundSettings]);
  useEffect(() => { userRef.current = user ?? null; }, [user]);

  const isRegisteredRef = useRef(isRegistered);
  const connectedEndpointIdRef = useRef(connectedEndpointId);
  const callStateRef = useRef(callState);
  useEffect(() => { isRegisteredRef.current = isRegistered; }, [isRegistered]);
  useEffect(() => { connectedEndpointIdRef.current = connectedEndpointId; }, [connectedEndpointId]);
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // Tracks the highest settingsVersion we've applied from a Pusher event so
  // stale out-of-order events are silently discarded.
  const lastAppliedSoundVersionRef = useRef<number>(-1);

  // Step 14: status_version guard — prevents stale out-of-order Pusher events from
  // briefly snapping an agent's status back to an old value.
  const lastSeenVersionRef = useRef<Map<string, number>>(new Map());

  // Seed the version map from the current server state on mount so the very first
  // Pusher event is only applied if it carries a newer version.
  useEffect(() => {
    fetch("/api/agent-statuses", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then((statuses: any[] | null) => {
        if (!Array.isArray(statuses)) return;
        for (const s of statuses) {
          if (s.endpointId && s.statusVersion != null) {
            const cur = lastSeenVersionRef.current.get(s.endpointId) ?? -1;
            lastSeenVersionRef.current.set(s.endpointId, Math.max(cur, s.statusVersion));
          }
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Super-admins without active impersonation have their platform-tenant tenantId,
    // not a client tenant — skip subscription to avoid spurious failed Pusher auth.
    if (user?.role === "super_admin" && !user?.impersonating) return;
    const tenantId = user?.impersonating?.tenantId || user?.tenantId;
    if (!tenantId) return;
    const convChannel = getConversationsChannel(tenantId);

    const invalidateInbox = () => {
      queryClient.invalidateQueries({ queryKey: ["/api/conversations/inbox"] });
    };

    const handleTeamChatChannelChange = () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/channels"] });
      queryClient.invalidateQueries({ queryKey: ["/api/team-chat/unread"] });
    };

    // Extends the existing new-message handler to also play a conversation sound.
    // This replaces the old useInboxSoundAlert (lastMessageAt-based) approach.
    const handleNewMessageConv = async (msg: NewMessagePayload) => {
      // Existing cache behaviour: always invalidate inbox
      invalidateInbox();

      const u = userRef.current;
      const uid: string = u?.id || "anon";
      if (!shouldPlayConversationMessageSound(
        msg,
        uid,
        Boolean(msg?.conversationId && isConversationActivelyViewed(uid, msg.conversationId)),
      )) return;

      const ss = soundSettingsRef.current;
      if (!ss) return; // settings not yet loaded — fail closed
      if (ss.conversationEnabled !== true) return;

      // Personal mute — read from localStorage at event time (no stale closure risk)
      if (localStorage.getItem(`snd_conv_muted_${uid}`) === "true") return;

      const vol = normalizeNotificationVolume(ss.conversationVolume);
      if (vol === 0) return;

      const msgId: string | undefined = msg?.id;
      if (!msgId) return;

      const effectiveTenantId: string =
        (u as any)?.impersonating?.tenantId || (u as any)?.tenantId || "";
      // User-scoped dedup key: two users in the same browser get independent alerts
      const dedupKey = `sound:message:${effectiveTenantId}:${uid}:${msgId}`;

      // navigator.locks — atomic cross-tab claim (preferred in modern browsers)
      if (typeof navigator !== "undefined" && (navigator as any).locks) {
        try {
          await (navigator as any).locks.request(
            dedupKey,
            { ifAvailable: true },
            async (lock: any) => {
              if (!lock) return; // another tab already claimed it
              const ss2 = soundSettingsRef.current;
              if (!ss2 || ss2.conversationEnabled !== true) return;
              if (localStorage.getItem(`snd_conv_muted_${uid}`) === "true") return;
              const vol2 = normalizeNotificationVolume(ss2.conversationVolume);
              if (vol2 > 0) playCustomOrBeep(vol2, ss2.conversationAudioUrl ?? null);
            },
          );
          return;
        } catch {}
      }

      // localStorage fallback for browsers without the Locks API
      const LS_MSG_DEDUP = "snd_msg_dedup";
      const MSG_DEDUP_TTL = 5 * 60 * 1000;
      try {
        const raw = localStorage.getItem(LS_MSG_DEDUP);
        const dmap: Record<string, number> = raw ? JSON.parse(raw) : {};
        const now = Date.now();
        if (dmap[dedupKey] && now - dmap[dedupKey] < MSG_DEDUP_TTL) return;
        for (const k of Object.keys(dmap)) if (now - dmap[k] > MSG_DEDUP_TTL) delete dmap[k];
        dmap[dedupKey] = now;
        localStorage.setItem(LS_MSG_DEDUP, JSON.stringify(dmap));
      } catch { return; }
      const ss2 = soundSettingsRef.current;
      if (!ss2 || ss2.conversationEnabled !== true) return;
      const vol2 = normalizeNotificationVolume(ss2.conversationVolume);
      if (vol2 > 0) playCustomOrBeep(vol2, ss2.conversationAudioUrl ?? null);
    };

    const handleConversationUnassigned = async (event: ConversationUnassignedPayload) => {
      invalidateInbox();
      if (!shouldNotifyForConversationUnassignment(event)) return;

      const ss = soundSettingsRef.current;
      const u = userRef.current;
      if (!ss || ss.conversationEnabled !== true) return;

      const uid: string = (u as any)?.id || "anon";
      if (localStorage.getItem(`snd_conv_muted_${uid}`) === "true") return;

      const vol = normalizeNotificationVolume(ss.conversationVolume);
      if (vol === 0 || !event.eventId) return;

      const effectiveTenantId: string =
        (u as any)?.impersonating?.tenantId || (u as any)?.tenantId || "";
      const dedupKey = `sound:unassigned:${effectiveTenantId}:${uid}:${event.eventId}`;

      if (typeof navigator !== "undefined" && (navigator as any).locks) {
        try {
          await (navigator as any).locks.request(
            dedupKey,
            { ifAvailable: true },
            async (lock: any) => {
              if (!lock) return;
              const currentSettings = soundSettingsRef.current;
              if (!currentSettings || currentSettings.conversationEnabled !== true) return;
              if (localStorage.getItem(`snd_conv_muted_${uid}`) === "true") return;
              const currentVolume = normalizeNotificationVolume(currentSettings.conversationVolume);
              if (currentVolume > 0) playCustomOrBeep(currentVolume, currentSettings.conversationAudioUrl ?? null);
            },
          );
          return;
        } catch {}
      }

      const LS_MSG_DEDUP = "snd_msg_dedup";
      const MSG_DEDUP_TTL = 5 * 60 * 1000;
      try {
        const raw = localStorage.getItem(LS_MSG_DEDUP);
        const dmap: Record<string, number> = raw ? JSON.parse(raw) : {};
        const now = Date.now();
        if (dmap[dedupKey] && now - dmap[dedupKey] < MSG_DEDUP_TTL) return;
        for (const k of Object.keys(dmap)) if (now - dmap[k] > MSG_DEDUP_TTL) delete dmap[k];
        dmap[dedupKey] = now;
        localStorage.setItem(LS_MSG_DEDUP, JSON.stringify(dmap));
      } catch { return; }

      const currentSettings = soundSettingsRef.current;
      if (!currentSettings || currentSettings.conversationEnabled !== true) return;
      const currentVolume = normalizeNotificationVolume(currentSettings.conversationVolume);
      if (currentVolume > 0) playCustomOrBeep(currentVolume, currentSettings.conversationAudioUrl ?? null);
    };

    if (convChannel) {
      convChannel.bind("new-message", handleNewMessageConv);
      convChannel.bind("conversation-updated", invalidateInbox);
      convChannel.bind("conversation-unassigned", handleConversationUnassigned);
      convChannel.bind("message-status-updated", invalidateInbox);
      convChannel.bind("new-livechat-session", invalidateInbox);
      convChannel.bind("team-chat-channel-created", handleTeamChatChannelChange);
      convChannel.bind("team-chat-channel-deleted", handleTeamChatChannelChange);
    }

    const unsubReconnectInbox = subscribeToReconnect(() => {
      queryClient.invalidateQueries({ queryKey: ["/api/conversations/inbox"] });
    });

    return () => {
      if (convChannel) {
        convChannel.unbind("new-message", handleNewMessageConv);
        convChannel.unbind("conversation-updated", invalidateInbox);
        convChannel.unbind("conversation-unassigned", handleConversationUnassigned);
        convChannel.unbind("message-status-updated", invalidateInbox);
        convChannel.unbind("new-livechat-session", invalidateInbox);
        convChannel.unbind("team-chat-channel-created", handleTeamChatChannelChange);
        convChannel.unbind("team-chat-channel-deleted", handleTeamChatChannelChange);
      }
      releaseConversationsChannel(tenantId);
      unsubReconnectInbox();
    };
  }, [user?.tenantId, user?.impersonating?.tenantId]);

  useEffect(() => {
    const tenantId = user?.impersonating?.tenantId || user?.tenantId;
    if (!tenantId) return;

    const channel = getTenantChannel(tenantId);
    if (!channel) return;

    const handleAgentStatus = (payload: AgentStatusChangedPayload) => {
      if (payload.tenantId !== tenantId) return;
      // Reconcile the sidebar independently: the dashboard's version cursor
      // may already know this event while the endpoint query is still older.
      applyEndpointAgentStatus(queryClient, payload);
      // Step 14: status_version guard — drop stale out-of-order Pusher events.
      if (payload.statusVersion != null) {
        const last = lastSeenVersionRef.current.get(payload.endpointId) ?? -1;
        if (payload.statusVersion <= last) return; // stale — discard
        lastSeenVersionRef.current.set(payload.endpointId, payload.statusVersion);
      }

      // NOTE: cancelQueries was removed here. It was canceling in-flight refetches
      // that were legitimately bringing live-call data into the cache, causing the
      // Live Calls section to flash empty then refill. The setQueryData patch below
      // is applied synchronously and takes effect before any concurrent refetch can
      // overwrite it (refetches are async); staleTime:5000 prevents eager re-fetches.
      queryClient.setQueryData<any>(["/api/dashboard-stats"], (old: any) => {
        if (!old) return old;
        const updatedDetails = (old.agents?.details ?? []).map((a: any) =>
          a.endpointId === payload.endpointId
            ? { ...a, status: payload.status, customLabel: payload.customLabel, updatedAt: payload.updatedAt }
            : a
        );
        const countStatus = (s: string) => updatedDetails.filter((a: any) => a.status === s).length;
        return {
          ...old,
          agents: {
            ...old.agents,
            ready: countStatus("ready"),
            onCall: countStatus("on_call"),
            afterCallWork: countStatus("after_call_work"),
            notReady: countStatus("not_ready"),
            loggedIn: updatedDetails.filter((a: any) => a.status !== "offline").length,
            details: updatedDetails,
          },
        };
      });
      // Surgical patch — covers the base key AND all parameterized variants of the
      // agents endpoint (timezone/direction/queueIds/agentIds filters all produce
      // different query keys; a single key match would miss them all).
      // Row shape: { userId, endpointId, status, customLabel, statusChangedAt, ... }
      // Payload shape: { endpointId, status, customLabel, updatedAt, tenantId }
      // Match by endpointId (stable, present in both payload and row).
      const agentPatch = { status: payload.status, customLabel: payload.customLabel, statusChangedAt: payload.updatedAt };
      const patchAgentRow = (old: any): any => {
        if (!old) return old;
        if (Array.isArray(old)) return old.map((a: any) => a.endpointId === payload.endpointId ? { ...a, ...agentPatch } : a);
        if (old?.data && Array.isArray(old.data)) return { ...old, data: old.data.map((a: any) => a.endpointId === payload.endpointId ? { ...a, ...agentPatch } : a) };
        return old;
      };
      queryClient.getQueryCache().findAll({
        predicate: (q) =>
          typeof q.queryKey[0] === "string" &&
          (q.queryKey[0] as string).startsWith("/api/realtime-dashboard/agents"),
      }).forEach((q) => queryClient.setQueryData(q.queryKey, patchAgentRow));

      // Safety-net: if the Pusher patch missed (endpointId mismatch across
      // multi-device registrations), schedule a delayed invalidation so the
      // dashboard still shows the correct status after the server cache clears.
      // The server now invalidates the dashboard cache immediately on every
      // lifecycle transition (no longer debounced), so 2 s is ample headroom.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/dashboard-stats"] });
      }, 2000);
    };

    const handleDashboardStats = (payload: DashboardStatsChangedPayload) => {
      // Use endedAt (not status string) to classify call phase — the server emits
      // actual call-log statuses ("in-progress", "ringing", "queued", etc.) which
      // don't include "answer". endedAt is null while the call is live, non-null
      // when it has finished.
      const isCallEnd = !!payload.endedAt;

      if (isCallEnd) {
        // For inbound queue calls the server creates TWO call logs: a queue leg and
        // a bridge leg. When the agent answers, the queue leg ends (endedAt set) and
        // the bridge leg starts (endedAt null). If we surgically remove the queue-leg
        // call from liveCalls immediately, the Live Calls panel flashes empty for ~4s
        // until the bridge-leg refetch completes.
        //
        // Instead: patch only the today.completed counter (for UI responsiveness) and
        // schedule a short refetch so the server returns the correct live state.
        queryClient.setQueryData<any>(["/api/dashboard-stats"], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            today: payload.status === "completed"
              ? { ...old.today, completed: (old.today?.completed ?? 0) + 1 }
              : old.today,
          };
        });
        // Refetch after 1.5 s so the server cache has been refreshed and
        // live calls reflects the true post-answer state (bridge leg or empty).
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/dashboard-stats"] });
        }, 1500);
        // Missed-call and voicemail notification bell updates are handled via
        // the server-emitted notification-created event (see storage.ts updateCallLog).
      } else {
        // Active call (new or updated while live). Use callLogId as dedup key.
        // If this id is already in liveCalls.calls it's an update — no refetch needed.
        // If it's a new call, invalidate so liveCalls.calls picks up the full row
        // (payload only carries id/status/startedAt — not the full call record).
        const existingCalls: any[] = (queryClient.getQueryData<any>(["/api/dashboard-stats"]) as any)?.liveCalls?.calls ?? [];
        const alreadyTracked = existingCalls.some((c: any) => c.id === payload.callLogId);
        if (!alreadyTracked) {
          // Delay by 2 s to ensure the server has completed its cache invalidation
          // before we refetch. The server now invalidates immediately on call events.
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["/api/dashboard-stats"] });
          }, 4000);
        }
      }

      // Always refresh the active-calls list so the realtime panel stays current
      queryClient.invalidateQueries({
        predicate: (q) =>
          typeof q.queryKey[0] === "string" &&
          (q.queryKey[0] as string).startsWith("/api/realtime-dashboard/active-calls"),
      });
    };

    const handleSubscriptionError = () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard-stats"] });
    };

    // ── Queue no-agent sound (via Pusher) ────────────────────────────────
    // The atomic dedup claim is acquired AFTER the 3-second delay so a tab that
    // closes during the delay does not prevent remaining tabs from playing.
    const queueKeyTimers = new Map<string, ReturnType<typeof setTimeout>>();
    let bc: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      try { bc = new BroadcastChannel("queue-sound-dedup"); } catch {}
    }

    const handleQueueNoAgent = (data: QueueNoAgentWaitingPayload) => {
      const ss = soundSettingsRef.current;
      const u = userRef.current;
      if (!ss || !u) return;
      if (ss.queueEnabled !== true) return;

      const userRole = (u as any).role as string;
      const allowedRoles: string[] = ss.queueSoundRoles ?? ["tenant_admin", "supervisor"];
      if (!allowedRoles.includes(userRole)) return;

      const allowedQueueIds: string[] = ss.queueIds ?? [];
      if (allowedQueueIds.length > 0 && !allowedQueueIds.includes(data.queueId)) return;

      const uid: string = (u as any).id || "anon";
      const myTenantId: string =
        (u as any)?.impersonating?.tenantId || (u as any)?.tenantId || "";
      // User-scoped key: different users in the same browser get independent alerts
      const dedupKey = `sound:queue:${myTenantId}:${uid}:${data.queueId}:${data.callUuid}`;

      if (queueKeyTimers.has(dedupKey)) return; // already scheduled in this tab

      const timer = setTimeout(async () => {
        queueKeyTimers.delete(dedupKey);

        // Re-read all settings after the delay — admin may have changed them
        const ss2 = soundSettingsRef.current;
        const u2 = userRef.current;
        if (!ss2 || !u2) return;
        if (ss2.queueEnabled !== true) return;
        const allowedRoles2: string[] = ss2.queueSoundRoles ?? ["tenant_admin", "supervisor"];
        if (!allowedRoles2.includes((u2 as any).role as string)) return;
        const allowedQueueIds2: string[] = ss2.queueIds ?? [];
        if (allowedQueueIds2.length > 0 && !allowedQueueIds2.includes(data.queueId)) return;
        const vol = normalizeNotificationVolume(ss2.queueVolume);
        if (vol === 0) return;
        const qAudioUrl: string | null = ss2.queueAudioUrl ?? ss2.defaultQueueAudioUrl ?? null;

        // navigator.locks — atomic cross-tab claim (preferred)
        if (typeof navigator !== "undefined" && (navigator as any).locks) {
          try {
            await (navigator as any).locks.request(
              dedupKey,
              { ifAvailable: true },
              async (lock: any) => {
                if (!lock) return;
                const ss3 = soundSettingsRef.current;
                if (!ss3 || ss3.queueEnabled !== true) return;
                const vol3 = normalizeNotificationVolume(ss3.queueVolume);
                if (vol3 > 0) {
                  const url3: string | null =
                    ss3.queueAudioUrl ?? ss3.defaultQueueAudioUrl ?? null;
                  playCustomOrBeep(vol3, url3);
                }
              },
            );
            return;
          } catch {}
        }

        // localStorage + BroadcastChannel fallback
        try {
          const raw = localStorage.getItem(QUEUE_DEDUP_LS_KEY_APP);
          const lsMap: Record<string, number> = raw ? JSON.parse(raw) : {};
          const now = Date.now();
          if (lsMap[dedupKey] && now - lsMap[dedupKey] < QUEUE_DEDUP_TTL_APP) return;
          for (const k of Object.keys(lsMap))
            if (now - lsMap[k] > QUEUE_DEDUP_TTL_APP) delete lsMap[k];
          lsMap[dedupKey] = now;
          localStorage.setItem(QUEUE_DEDUP_LS_KEY_APP, JSON.stringify(lsMap));
        } catch { return; }
        bc?.postMessage({ type: "dedup", key: dedupKey });
        playCustomOrBeep(vol, qAudioUrl);
      }, 3000);

      queueKeyTimers.set(dedupKey, timer);
    };

    // ── Sound settings changed (via Pusher) ──────────────────────────────
    // Updates the React Query cache immediately so all components using
    // soundSettings see the new values without a page reload.
    const handleSoundSettingsChanged = (
      payload: SoundNotificationSettingsChangedPayload,
    ) => {
      const myTenantId =
        userRef.current?.impersonating?.tenantId || userRef.current?.tenantId;
      if (payload.tenantId !== myTenantId) return;
      // Drop stale out-of-order events
      if (payload.settingsVersion <= lastAppliedSoundVersionRef.current) return;
      lastAppliedSoundVersionRef.current = payload.settingsVersion;

      queryClient.setQueryData<any>(["/api/notification-settings/sound"], {
        conversationEnabled: payload.conversationEnabled,
        conversationVolume: payload.conversationVolume,
        conversationAudioUrl: payload.conversationAudioUrl,
        queueEnabled: payload.queueEnabled,
        queueVolume: payload.queueVolume,
        queueSoundRoles: payload.queueSoundRoles,
        queueIds: payload.queueIds,
        queueAudioUrl: payload.queueAudioUrl,
        defaultQueueAudioUrl: "/sounds/queue-default-ding.mp3",
        settingsVersion: payload.settingsVersion,
      });

      // Cancel all pending Queue timers when queue sound is disabled
      if (!payload.queueEnabled) {
        for (const t of queueKeyTimers.values()) clearTimeout(t);
        queueKeyTimers.clear();
      }
    };
    // ────────────────────────────────────────────────────────────────────

    const handleTicketUpdated = (payload: TicketUpdatedPayload) => {
      const patch: Record<string, unknown> = {};
      if (payload.status !== undefined) patch.status = payload.status;
      if (payload.priority !== undefined) patch.priority = payload.priority;
      if (payload.assigneeUserId !== undefined) patch.assigneeUserId = payload.assigneeUserId;
      if (payload.unreadCount !== undefined) patch.unreadCount = payload.unreadCount;
      if (payload.lastMessageAt !== undefined) patch.lastMessageAt = payload.lastMessageAt;
      if (payload.action === "created") {
        // New ticket — invalidate all list caches so it appears immediately
        queryClient.invalidateQueries({
          predicate: (q) =>
            typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/tickets"),
        });
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      } else {
        if (Object.keys(patch).length > 0) {
          // Surgical patch: update ticket detail and any list caches in place
          queryClient.setQueryData<any>(["/api/tickets", payload.ticketId], (old: any) =>
            old ? { ...old, ...patch } : old
          );
          queryClient.getQueryCache().findAll({
            predicate: (q) =>
              typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/tickets?"),
          }).forEach((q) => {
            queryClient.setQueryData<any>(q.queryKey, (old: any) => {
              if (!old?.data) return old;
              return {
                ...old,
                data: old.data.map((t: any) =>
                  t.id === payload.ticketId ? { ...t, ...patch } : t
                ),
              };
            });
          });
        }
        // A reply adds a new message to the thread — refresh the messages sub-query
        // so an open ticket detail panel shows the new message without manual reload.
        if (payload.action === "replied") {
          queryClient.invalidateQueries({
            queryKey: ["/api/tickets", payload.ticketId, "messages"],
          });
        }
      }
    };

    const handleNotificationCreated = (payload: NotificationCreatedPayload) => {
      if (payload.notification?.type === "voicemail") {
        // Voicemail notifications are lifecycle-based rather than per-user
        // unread items. Recalculate both tenant-wide counts from authoritative
        // voicemail state instead of incrementing the cached batch locally.
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
        return;
      }
      if (payload.notification) {
        const n = payload.notification;
        queryClient.setQueryData<any>(["/api/notifications"], (old: any) => {
          if (!old) return old;
          // If a batch item of the same type already exists, increment its count
          // (the bell shows aggregated batches, not individual records)
          const existingIdx = (old.notifications ?? []).findIndex((x: any) => x.type === n.type);
          if (existingIdx >= 0) {
            const notifications = [...old.notifications];
            notifications[existingIdx] = {
              ...notifications[existingIdx],
              count: notifications[existingIdx].count + 1,
              timestamp: n.timestamp,
              preview: n.preview || notifications[existingIdx].preview,
            };
            return { ...old, notifications, totalCount: (old.totalCount ?? 0) + 1 };
          }
          // No existing batch for this type — prepend as a new item
          return {
            ...old,
            notifications: [n, ...(old.notifications ?? [])],
            totalCount: (old.totalCount ?? 0) + 1,
          };
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      }
    };

    // sip-reregister is now handled inside PlivoProvider (plivo-context.tsx)
    // to keep SIP resilience logic collocated with the SDK lifecycle.

    const handleTenantSettingsChanged = async (payload: TenantSettingsChangedPayload) => {
      // Always freshen the settings cache so every UI component sees the new values.
      queryClient.invalidateQueries({ queryKey: ["/api/tenant-settings"] });

      // If this agent is currently registered and idle, apply the new default
      // login status immediately so it takes effect without requiring a re-login.
      const epId = connectedEndpointIdRef.current;
      const newStatus = payload.statusAfterLogin;
      if (
        isRegisteredRef.current &&
        epId &&
        newStatus &&
        callStateRef.current === "idle"
      ) {
        try {
          const { apiRequest } = await import("@/lib/queryClient");
          await apiRequest("PUT", `/api/agent-statuses/${epId}`, resolveAgentLoginStatus(newStatus));
        } catch (_err) {
          // Best-effort — status will be corrected on next login.
        }
      }
    };

    // Handler for per-agent rejection events — only invalidates dashboard-stats cache.
    // Must NOT be treated as a call lifecycle event (no live-calls mutation, no
    // completed-counter increment).
    const handleAgentAttemptChanged = () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard-stats"] });
    };

    channel.bind("agent-status-changed", handleAgentStatus);
    channel.bind("dashboard-stats-changed", handleDashboardStats);
    channel.bind("dashboard-agent-attempt-changed", handleAgentAttemptChanged);
    channel.bind("pusher:subscription_error", handleSubscriptionError);
    channel.bind("queue-no-agent-waiting", handleQueueNoAgent);
    channel.bind("ticket-updated", handleTicketUpdated);
    channel.bind("notification-created", handleNotificationCreated);
    channel.bind("tenant-settings-changed", handleTenantSettingsChanged);
    channel.bind("sound-notification-settings-changed", handleSoundSettingsChanged);
    // sip-reregister is bound inside PlivoProvider — no binding here.

    const pusher = (channel as any).pusher;
    const handleReconnect = () => {
      // On Pusher reconnect, broadly resync all stale caches to recover from
      // any events missed during the disconnection window.
      queryClient.invalidateQueries({ stale: true });
      // Explicitly refetch sound settings — staleTime:Infinity means the query
      // won't be included in the stale-only sweep above, but we need the latest
      // settings in case a settings-changed event was missed while disconnected.
      queryClient.invalidateQueries({ queryKey: ["/api/notification-settings/sound"] });
    };
    pusher?.connection?.bind("connected", handleReconnect);

    return () => {
      channel.unbind("agent-status-changed", handleAgentStatus);
      channel.unbind("dashboard-stats-changed", handleDashboardStats);
      channel.unbind("dashboard-agent-attempt-changed", handleAgentAttemptChanged);
      channel.unbind("pusher:subscription_error", handleSubscriptionError);
      channel.unbind("queue-no-agent-waiting", handleQueueNoAgent);
      channel.unbind("ticket-updated", handleTicketUpdated);
      channel.unbind("notification-created", handleNotificationCreated);
      channel.unbind("tenant-settings-changed", handleTenantSettingsChanged);
      channel.unbind("sound-notification-settings-changed", handleSoundSettingsChanged);
      // sip-reregister unbind is handled by PlivoProvider cleanup.
      pusher?.connection?.unbind("connected", handleReconnect);
      bc?.close();
      for (const t of queueKeyTimers.values()) clearTimeout(t);
      queueKeyTimers.clear();
      releaseTenantChannel(tenantId);
    };
  }, [user?.tenantId, user?.impersonating?.tenantId]);

  return null;
}

function AudioContextUnlocker() {
  useEffect(() => {
    const unlock = () => unlockAudioContext();
    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);
  return null;
}

const CONV_TAB_PREV_STATUS_KEY = "conv_tab_prev_status";
const CONV_TAB_PREV_LABEL_KEY = "conv_tab_prev_label";
const SYSTEM_STATUSES_CONV = ["ready", "not_ready", "on_call", "after_call_work", "offline"];

/** Returns true for any conversations-related page that should trigger the auto-status feature. */
function isConversationsPage(path: string) {
  return path.startsWith("/conversations") || path.startsWith("/conversation-history") || path.startsWith("/conversations/inbox") || path.startsWith("/conversations/history");
}

function ConversationsTabStatusEffect() {
  const [location] = useLocation();
  const { callState, connectedEndpointId } = usePlivo();
  const { user } = useAuth();
  const prevLocationRef = useRef<string>("");
  const callStateRef = useRef(callState);
  const connectedEndpointIdRef = useRef(connectedEndpointId);
  const tenantId = user?.impersonating?.tenantId || user?.tenantId;

  useEffect(() => { callStateRef.current = callState; }, [callState]);
  useEffect(() => { connectedEndpointIdRef.current = connectedEndpointId; }, [connectedEndpointId]);

  // Pusher listener: cancel the auto-revert if the agent manually changes their own
  // status while on a conversations page. The payload now carries changeSource so we
  // can distinguish system-initiated writes from agent/supervisor-manual ones.
  useEffect(() => {
    if (!tenantId) return;
    const channel = getTenantChannel(tenantId);
    if (!channel) return;

    const handleStatusChange = (payload: AgentStatusChangedPayload) => {
      const epId = connectedEndpointIdRef.current;
      if (!epId || payload.endpointId !== epId) return;
      // Only act while we're on a conversations page (revert is pending)
      if (!isConversationsPage(window.location.pathname)) return;
      // If the change was NOT our system auto-set, the agent overrode it manually —
      // clear the saved state so we don't revert when they leave.
      if (payload.changeSource !== "system_conversations_tab") {
        sessionStorage.removeItem(CONV_TAB_PREV_STATUS_KEY);
        sessionStorage.removeItem(CONV_TAB_PREV_LABEL_KEY);
        console.log("[ConvTabStatus] Manual status change detected via Pusher — auto-revert cancelled");
      }
    };

    channel.bind("agent-status-changed", handleStatusChange);
    return () => {
      channel.unbind("agent-status-changed", handleStatusChange);
      releaseTenantChannel(tenantId);
    };
  }, [tenantId]);

  // Location-change effect: set status on enter, revert on leave.
  useEffect(() => {
    const prev = prevLocationRef.current;
    prevLocationRef.current = location;

    const isNowOnConversations = isConversationsPage(location);
    const wasOnConversations = isConversationsPage(prev);

    const epId = connectedEndpointIdRef.current;
    const role = user?.role;

    if (!epId || !role || role === "super_admin") return;

    if (!wasOnConversations && isNowOnConversations) {
      if (callStateRef.current !== "idle") return;

      (async () => {
        try {
          const [settingsRes, statusRes] = await Promise.all([
            fetch("/api/tenant-settings", { credentials: "include" }),
            fetch(`/api/agent-statuses/${epId}`, { credentials: "include" }),
          ]);
          if (!settingsRes.ok || !statusRes.ok) return;

          const [settings, status] = await Promise.all([settingsRes.json(), statusRes.json()]);

          const tbs = settings?.tenantBusinessSettings;
          if (!tbs?.conversationsTabStatusEnabled) return;

          const targetStatus: string = tbs.conversationsTabStatus || "not_ready";
          const currentRaw: string = status.status || "offline";
          const currentLabel: string | null = status.customLabel || null;

          if (currentRaw === "on_call" || currentRaw === "ringing" || currentRaw === "offline") return;

          const isAlreadyTarget = SYSTEM_STATUSES_CONV.includes(targetStatus)
            ? currentRaw === targetStatus
            : currentRaw === "not_ready" && currentLabel === targetStatus;

          if (isAlreadyTarget) return;

          sessionStorage.setItem(CONV_TAB_PREV_STATUS_KEY, currentRaw);
          if (currentLabel) sessionStorage.setItem(CONV_TAB_PREV_LABEL_KEY, currentLabel);
          else sessionStorage.removeItem(CONV_TAB_PREV_LABEL_KEY);

          const { apiRequest: req, queryClient: qc } = await import("@/lib/queryClient");
          const isSystem = SYSTEM_STATUSES_CONV.includes(targetStatus);
          await req("PUT", `/api/agent-statuses/${epId}`, {
            status: isSystem ? targetStatus : "not_ready",
            customLabel: isSystem ? null : targetStatus,
            changeSource: "system_conversations_tab",
          });
          qc.invalidateQueries({ queryKey: ["/api/agent-statuses"] });
        } catch (e) {
          console.error("[ConvTabStatus] Failed to set conversations tab status:", e);
        }
      })();

    } else if (wasOnConversations && !isNowOnConversations) {
      const prevStatus = sessionStorage.getItem(CONV_TAB_PREV_STATUS_KEY);
      const prevLabel = sessionStorage.getItem(CONV_TAB_PREV_LABEL_KEY);

      sessionStorage.removeItem(CONV_TAB_PREV_STATUS_KEY);
      sessionStorage.removeItem(CONV_TAB_PREV_LABEL_KEY);

      if (!prevStatus) return;
      if (callStateRef.current !== "idle") return;

      (async () => {
        try {
          const { apiRequest: req, queryClient: qc } = await import("@/lib/queryClient");
          await req("PUT", `/api/agent-statuses/${epId}`, {
            status: prevStatus,
            customLabel: prevLabel || null,
            changeSource: "system_conversations_tab",
          });
          qc.invalidateQueries({ queryKey: ["/api/agent-statuses"] });
        } catch (e) {
          console.error("[ConvTabStatus] Failed to revert conversations tab status:", e);
        }
      })();
    }
  }, [location, user?.role]);

  return null;
}

function AuthenticatedApp() {
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <PlivoProvider>
      <NavigationGuardProvider>
      <DialpadStateProvider>
        <KbSearchStateProvider>
        <GlobalSearchStateProvider>
        <CopilotStateProvider>
        <TeamChatStateProvider>
        <AudioContextUnlocker />
        <SipAutoConnect />
        <ConversationsTabStatusEffect />
        <NavigationGuardDialog />
        <GlobalInboxSoundMonitor />
        <BrowserNotificationMonitor />
        <GlobalMessageToastMonitor />
        <SidebarProvider style={style as React.CSSProperties}>
          <div className="flex h-screen w-full">
              <AppSidebar />
              <div className="flex flex-col flex-1 min-w-0">
                <ImpersonationBanner />
                <header className="flex items-center justify-between gap-2 p-2 border-b sticky top-0 z-50 bg-background">
                  <SidebarTrigger data-testid="button-sidebar-toggle" />
                  <div className="flex items-center gap-2">
                    <CampaignCallIndicator />
                    <QuickActionsHeaderButton />
                    <CopilotHeaderButton />
                    <GlobalSearchHeaderButton />
                    <TeamChatHeaderButton />
                    <KbSearchHeaderButton />
                    <DialpadHeaderButton />
                    <NotificationBell />
                    <ThemeToggle />
                    <UserHeaderInfo />
                  </div>
                </header>
                <BrowserNotificationBanner />
                <main className="flex-1 min-h-0 overflow-auto">
                  <Router />
                </main>
              </div>
          </div>
        </SidebarProvider>
        <FloatingDialpad />
        <FloatingCallScript />
        <MonitorCallPanel />
        <FloatingKbSearch />
        <FloatingTeamChat />
        <FloatingGlobalSearch />
        <PostCallDisposition />
        <FloatingCopilot />
        <SupportChatWidget />
      </TeamChatStateProvider>
      </CopilotStateProvider>
        </GlobalSearchStateProvider>
      </KbSearchStateProvider>
      </DialpadStateProvider>
      </NavigationGuardProvider>
    </PlivoProvider>
  );
}

function UnauthenticatedApp() {
  const { twoFactorPending, clearTwoFactor } = useAuth();
  const [authView, setAuthView] = useState<"login" | "forgot">("login");
  const [location, navigate] = useLocation();

  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("token");
  const isResetPage = location === "/reset-password" && resetToken;

  const goToLogin = () => {
    setAuthView("login");
    clearTwoFactor();
    if (location !== "/") {
      navigate("/");
    }
  };

  if (isResetPage && resetToken) {
    return <ResetPasswordPage token={resetToken} onBackToLogin={goToLogin} />;
  }

  if (twoFactorPending) {
    return <Verify2FAPage email={twoFactorPending.email} onBack={goToLogin} />;
  }

  if (authView === "forgot") {
    return <ForgotPasswordPage onBack={() => setAuthView("login")} />;
  }

  return <LoginPage onForgotPassword={() => setAuthView("forgot")} />;
}

function FaviconApplier() {
  const branding = usePlatformBranding();
  useEffect(() => {
    if (branding.appName) {
      document.title = branding.appName;
    }
  }, [branding.appName]);
  useEffect(() => {
    if (!branding.faviconUrl) return;
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }, [branding.faviconUrl]);
  return null;
}

function AppGate() {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();

  if (location === "/knowledgebase") {
    return <KnowledgeBase />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <UnauthenticatedApp />;
  }

  return <AuthenticatedApp />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <PermissionsProvider>
            <FaviconApplier />
            <Suspense fallback={(
              <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            )}>
              <AppGate />
            </Suspense>
            <Toaster />
          </PermissionsProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
